import React from 'react';
import { FaExclamationTriangle, FaUtensils } from 'react-icons/fa';
import { ECOSYSTEM_PROFILES } from '../data/ecosystemProfiles';
import './Ecosystem.css';

export const EcosystemSelector = ({ selectedEcosystem, onSelect }) => (
  <div className="ecosystem-selector">
    <h3>Select Ecosystem Profile</h3>
    <div className="ecosystem-grid">
      {ECOSYSTEM_PROFILES.map(eco => (
        <div 
          key={eco.id}
          className={`ecosystem-option ${selectedEcosystem?.id === eco.id ? 'active' : ''}`}
          onClick={() => onSelect(eco)}
        >
          <div 
            className="eco-image" 
            style={{ 
              backgroundImage: `url(${process.env.PUBLIC_URL}${eco.image})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center'
            }} 
          />
          <div className="eco-name">{eco.name}</div>
          <div className="eco-description">{eco.description}</div>
        </div>
      ))}
    </div>
  </div>
);
export const EcosystemVisualization = ({ ecosystem, selectedOrganism, onSelectOrganism, parameters }) => {
  const defaultMarineParams = {
    temperature: { min: 22, max: 28, unit: '°C' },
    salinity: { min: 32, max: 36, unit: 'ppt' },
    ph: { min: 7.8, max: 8.4, unit: '' },
    oxygen: { min: 5, max: 8, unit: 'mg/L' },
    flowRate: { min: 0.5, max: 2, unit: 'cm/s' },
    lightIntensity: { min: 40, max: 80, unit: '%' }
  };

  const feedingParams = {
    foodAmount: { min: 0.1, max: 0.5, unit: 'g/feeding' },
    feedingFrequency: { min: 2, max: 4, unit: 'times/day' },
    liveFood: { required: false }
  };

  const requiresFeeding = selectedOrganism?.needsFeeding || 
                         selectedOrganism?.name.toLowerCase().includes('seahorse') || 
                         selectedOrganism?.name.toLowerCase().includes('fish');

  const getAllParams = (organism) => {
    if (!organism) return defaultMarineParams;
    
    const baseParams = {
      ...defaultMarineParams,
      ...organism.idealParams
    };

    return requiresFeeding ? {
      ...baseParams,
      ...feedingParams,
      ...(organism.feedingRequirements || {})
    } : baseParams;
  };

  return (
    <div className="ecosystem-visualization">
      <div className="ecosystem-header">
        <h2>{ecosystem.name}</h2>
        <p className="ecosystem-description">{ecosystem.description}</p>
      </div>

      <div className="organism-selection-section">
        <h3>Select Organism</h3>
        <div className="organism-grid">
          {ecosystem.organisms.map((org, index) => (
            <div 
              key={`${org.name}-${index}`}
              className={`organism-card ${selectedOrganism?.name === org.name ? 'active' : ''}`}
              onClick={() => onSelectOrganism(org)}
            >
              <div 
                className="organism-image-container"
                style={{ 
                  backgroundImage: `url(${process.env.PUBLIC_URL}${org.image})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center'
                }}
              >
                {!org.image && <div className="image-placeholder">No Image</div>}
              </div>
              <div className="organism-name">{org.name}</div>
            </div>
          ))}
        </div>
      </div>

      {selectedOrganism && (
        <div className="organism-details-panel">
          <div className="organism-details-container">
            <div 
              className="selected-organism-image"
              style={{
                backgroundImage: `url(${process.env.PUBLIC_URL}${selectedOrganism.image})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center'
              }}
            ></div>
            
            <div className="parameters-container">
              <h3 className="organism-title">{selectedOrganism.name}</h3>
              
              <div className="parameter-grid">
                {Object.entries(getAllParams(selectedOrganism)).map(([param, range]) => {
                  if (parameters[param] === undefined) return null;
                  if (!requiresFeeding && param in feedingParams) return null;
                  
                  const currentValue = parameters[param];
                  const isOptimal = currentValue >= range.min && currentValue <= range.max;
                  
                  return (
                    <div 
                      key={param} 
                      className={`parameter-card ${isOptimal ? 'optimal' : 'warning'}`}
                    >
                      <div className="parameter-header">
                        <span className="parameter-title">{param}</span>
                        <span className="parameter-value">
                          {currentValue}{range.unit}
                        </span>
                      </div>
                      <div className="parameter-range">
                        Ideal range: {range.min}-{range.max}{range.unit}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {requiresFeeding && (
            <div className="feeding-requirements">
              <h4><FaUtensils /> Feeding Instructions</h4>
              {selectedOrganism.feedingInstructions ? (
                <p>{selectedOrganism.feedingInstructions}</p>
              ) : (
                <p>Feed {parameters.feedingFrequency || 2}-3 times daily with appropriate food</p>
              )}
              {selectedOrganism?.idealParams?.liveFood?.required && (
                <p className="live-food-warning">
                  <FaExclamationTriangle /> Requires live food
                </p>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};